import { PageHeader } from "@dmca/frontend/components/nav";
import { Button } from "@dmca/frontend/components/button";
import { useAppPermissions } from "@dmca/frontend/hooks";
import { useTRPC } from "@dmca/frontend/trpc";
import { HelpSections } from "@dmca/models/interface/help";
import { Container, Paper, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "react-toastify";

export const Route = createFileRoute("/_authed/help/add-new")({
  component: AddHelpPage,
  loader: async ({ context }) => {
    const { trpcQcProxy: trpc, qc } = context;
    await qc.ensureQueryData(trpc.help.getPages.queryOptions());
  },
  head: () => ({
    meta: [{ title: "Add Help Page | DNA Dashboard" }],
  }),
});

function AddHelpPage() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hasPermission = useAppPermissions();
  const { data: helpPages } = useQuery(trpc.help.getPages.queryOptions());
  const existingSections = new Set(
    helpPages?.pages.map((page) => page.section) ?? []
  );

  const createPageMutation = useMutation(
    trpc.help.createPage.mutationOptions({
      onError: (error) => {
        toast.error(error.message || "Failed to create help page.");
      },
      onSuccess: (page) => {
        toast.success("Help page created.");
        qc.invalidateQueries(trpc.help.getPages.queryFilter());
        navigate({ to: "/help/$section", params: { section: page.section } });
      },
    })
  );

  const manageableSections = HelpSections.filter((section) =>
    hasPermission(section.managePermission)
  );

  return (
    <Container>
      <PageHeader>Add Help Page</PageHeader>
      <Stack gap={2} sx={{ mt: 2 }}>
        {manageableSections.length === 0 && (
          <Typography>
            You do not have permission to create help pages.
          </Typography>
        )}
        {manageableSections.map((section) => {
          const exists = existingSections.has(section.id);
          return (
            <Paper key={section.id} sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                gap={1}
              >
                <Stack>
                  <Typography sx={{ fontWeight: 600 }}>
                    {section.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {exists
                      ? "Help page already exists."
                      : "Create a new help page."}
                  </Typography>
                </Stack>
                <Button
                  disabled={exists}
                  loading={createPageMutation.isPending}
                  onClick={() =>
                    createPageMutation.mutate({ section: section.id })
                  }
                >
                  Create
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Container>
  );
}
